import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Checkbox,
    FormControlLabel,
    SelectChangeEvent,
    Grid,
    Icon,
    Autocomplete,
    TextField,
    Chip,
} from '@mui/material';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';

import MDBox from 'components/MDBox';
import MDButton from 'components/MDButton';
import MDInput from 'components/MDInput';
import MDTypography from 'components/MDTypography';
import { request } from 'app/network';
import {
    DndContext,
    closestCenter,
    KeyboardSensor,
    PointerSensor,
    useSensor,
    useSensors,
    DragEndEvent,
} from '@dnd-kit/core';
import {
    arrayMove,
    SortableContext,
    sortableKeyboardCoordinates,
    verticalListSortingStrategy,
    useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import ImageCropModal from 'Screens/Advertisements/components/ImageCropModal/ImageCropModal';
import { uploadToS3 } from 'app/network/api/s3Upload';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import { resizeImage, convertFileToWebP } from 'utils/imageResize';

interface AddExerciseVideoModalProps {
    open: boolean;
    editingVideo?: any | null;
    onClose: () => void;
    onSuccess: () => void;
    darkMode?: boolean;
}

const AddExerciseVideoModal: React.FC<AddExerciseVideoModalProps> = ({ open, editingVideo, onClose, onSuccess, darkMode }) => {
    const [formData, setFormData] = useState({
        title: '',
        description: '',
        youtubeUrl: '',
        youtubeVideoId: '',
        thumbnailUrl: '',
        durationSec: '',

        displayOrder: '',
        pointPolicySeqId: '',
        isActive: true,
        isFeatured: false,
        bodyPartIds: [] as number[],
    });

    const [thumbnail34, setThumbnail34] = useState<{ url: string; file: File | null; preview: string }>({ url: '', file: null, preview: '' });
    const [thumbnail169, setThumbnail169] = useState<{ url: string; file: File | null; preview: string }>({ url: '', file: null, preview: '' });
    const [isDragging34, setIsDragging34] = useState(false);
    const [isDragging169, setIsDragging169] = useState(false);
    const [cropModalOpen, setCropModalOpen] = useState(false);
    const [tempImageForCrop, setTempImageForCrop] = useState('');
    const [activeCropRatio, setActiveCropRatio] = useState<number | null>(null); // 4/3 or 16/9

    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [pointPolicies, setPointPolicies] = useState<any[]>([]);
    const [fetchingVideoInfo, setFetchingVideoInfo] = useState(false);
    const [existingVideos, setExistingVideos] = useState<Array<{ seqId: number; title: string; displayOrder: number; thumbnailUrl?: string }>>([]);
    const [newVideoPosition, setNewVideoPosition] = useState(0);
    const [bodyParts, setBodyParts] = useState<any[]>([]);


    // 적립정책 목록 로드
    useEffect(() => {
        if (open) {
            loadPointPolicies();
            loadExistingVideos();
            loadBodyParts();
        }
    }, [open]);

    // 편집 모드: editingVideo 데이터로 formData 초기화
    useEffect(() => {
        if (open && editingVideo) {
            setThumbnail34({ url: editingVideo.thumbnailUrl43 || '', file: null, preview: editingVideo.thumbnailUrl43 || '' });
            setThumbnail169({ url: editingVideo.thumbnailUrl169 || '', file: null, preview: editingVideo.thumbnailUrl169 || '' });

            const formatDuration = (seconds?: number) => {
                if (!seconds) return '';
                const hours = Math.floor(seconds / 3600);
                const mins = Math.floor((seconds % 3600) / 60);
                const secs = seconds % 60;
                return `${hours.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
            };

            setFormData({
                title: editingVideo.title || '',
                description: editingVideo.description || '',
                youtubeUrl: editingVideo.youtubeUrl || '',
                youtubeVideoId: editingVideo.youtubeVideoId || '',
                thumbnailUrl: editingVideo.thumbnailUrl || '',
                durationSec: formatDuration(editingVideo.durationSec),

                displayOrder: editingVideo.displayOrder?.toString() || '',
                pointPolicySeqId: editingVideo.pointPolicySeqId?.toString() || '',
                isActive: editingVideo.isActive !== undefined ? editingVideo.isActive : true,
                isFeatured: editingVideo.isFeatured || false,
                bodyPartIds: editingVideo.bodyPartIds || [],
            });

            // 편집 모드에서는 newVideoPosition을 설정하지 않음 (기존 위치 유지)
        } else if (open && !editingVideo) {
            // 추가 모드: formData 초기화
            setFormData({
                title: '',
                description: '',
                youtubeUrl: '',
                youtubeVideoId: '',
                thumbnailUrl: '',
                durationSec: '',

                displayOrder: '',
                pointPolicySeqId: '',
                isActive: true,
                isFeatured: false,
                bodyPartIds: [],
            });
            setThumbnail34({ url: '', file: null, preview: '' });
            setThumbnail169({ url: '', file: null, preview: '' });
        }
    }, [open, editingVideo]);

    const loadPointPolicies = async () => {
        try {
            const SELECTED_TENANT_KEY = "mediinstep_selected_tenant";
            const savedTenantId = localStorage.getItem(SELECTED_TENANT_KEY);

            const res = await request('PointPolicy.list', {
                paramz: {
                    tenantSeqId: savedTenantId ? parseInt(savedTenantId, 10) : undefined,
                    policyType: 'VIDEO_EARN',
                }
            });

            if (res && res.data) {
                // Sort by SeqId descending (latest first)
                const sortedPolicies = [...res.data].sort((a: any, b: any) => b.seqId - a.seqId);
                setPointPolicies(sortedPolicies);

                // Auto-select the latest policy if adding a new video or if editing and no policy set
                if (!editingVideo) {
                    if (sortedPolicies.length > 0) {
                        setFormData(prev => ({
                            ...prev,
                            pointPolicySeqId: sortedPolicies[0].seqId.toString()
                        }));
                    }
                }
            }
        } catch (err) {
            console.error('적립정책 목록 조회 실패:', err);
        }
    };

    const loadExistingVideos = async () => {
        try {
            const SELECTED_TENANT_KEY = "mediinstep_selected_tenant";
            const savedTenantId = localStorage.getItem(SELECTED_TENANT_KEY);

            const response = await request('ExerciseVideo.pagedList', {
                paramz: {
                    PageNumber: 1,
                    PageSize: 100,
                    TenantSeqId: savedTenantId ? parseInt(savedTenantId, 10) : undefined,
                },
            });

            if (response && response.data) {
                const items = response.data.items || response.data; // Handle both paged and list structure just in case
                if (Array.isArray(items)) {
                    const existingVideos = items
                        .map((video: any) => ({
                            seqId: video.seqId,
                            title: video.title || '제목 없음',
                            displayOrder: video.displayOrder || 0,
                            thumbnailUrl: video.thumbnailUrl,
                        }))
                        .sort((a: any, b: any) => a.displayOrder - b.displayOrder);

                    setExistingVideos(existingVideos);
                    setNewVideoPosition(existingVideos.length);
                }
            }
        } catch (err) {
            console.error('기존 영상 목록 조회 실패:', err);
        }
    };

    const loadBodyParts = async () => {
        try {
            const res = await request('BodyPart.pagedList', {
                paramz: {
                    PageNumber: 1,
                    PageSize: 1000,
                }
            });
            if (res && res.data && res.data.items) {
                setBodyParts(res.data.items);
            }
        } catch (err) {
            console.error('Failed to load body parts:', err);
        }
    };


    // YouTube URL에서 Video ID 추출
    const extractYouTubeVideoId = (url: string): string | null => {
        if (!url) return null;

        // 다양한 YouTube URL 형식 지원
        const patterns = [
            /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([^&\n?#]+)/,
            /^([a-zA-Z0-9_-]{11})$/, // 직접 Video ID 입력
        ];

        for (const pattern of patterns) {
            const match = url.match(pattern);
            if (match && match[1]) {
                return match[1];
            }
        }
        return null;
    };

    // YouTube URL 변경 시 자동으로 Video ID와 썸네일 추출
    useEffect(() => {
        const fetchVideoInfo = async () => {
            if (formData.youtubeUrl) {
                const videoId = extractYouTubeVideoId(formData.youtubeUrl);
                if (videoId) {
                    // 기본 정보 설정
                    setFormData(prev => ({
                        ...prev,
                        youtubeVideoId: videoId,
                        thumbnailUrl: `https://img.youtube.com/vi/${videoId}/mqdefault.jpg`,
                    }));

                    // YouTube API로 상세 정보 가져오기
                    setFetchingVideoInfo(true);
                    try {
                        const { fetchYouTubeVideoInfo } = await import('utils/youtubeApi');
                        const videoInfo = await fetchYouTubeVideoInfo(videoId);

                        console.log('📺 모달에서 받은 videoInfo:', videoInfo);

                        if (videoInfo) {
                            console.log('📝 업데이트할 데이터:', {
                                title: videoInfo.title,
                                description: videoInfo.description?.substring(0, 100),
                                durationSec: videoInfo.durationSeconds,
                            });

                            setFormData(prev => ({
                                ...prev,
                                title: prev.title || videoInfo.title, // 제목이 비어있을 때만 자동 입력
                                description: prev.description || videoInfo.description,
                                thumbnailUrl: videoInfo.thumbnailUrl,
                                durationSec: videoInfo.durationSeconds.toString(),
                            }));
                        } else {
                            console.warn('⚠️ videoInfo가 null입니다.');
                        }
                    } catch (err) {
                        console.error('❌ YouTube API 호출 실패 (기본 정보만 사용):', err);
                    } finally {
                        setFetchingVideoInfo(false);
                    }
                }
            }
        };

        fetchVideoInfo();
    }, [formData.youtubeUrl]);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleSelectChange = (name: string) => (e: SelectChangeEvent) => {
        setFormData(prev => ({ ...prev, [name]: e.target.value }));
    };

    const handleCheckboxChange = (name: string) => (e: React.ChangeEvent<HTMLInputElement>) => {
        setFormData(prev => ({ ...prev, [name]: e.target.checked }));
    };

    const resizeImage = (file: File, maxWidth: number, maxHeight: number): Promise<File> => {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = (e) => {
                const img = new Image();
                img.onload = () => {
                    const aspectRatio = img.width / img.height;
                    let width = img.width;
                    let height = img.height;

                    if (width > maxWidth || height > maxHeight) {
                        if (width / maxHeight > aspectRatio) {
                            width = maxWidth;
                            height = width / aspectRatio;
                        } else {
                            height = maxHeight;
                            width = height * aspectRatio;
                        }
                    }

                    const canvas = document.createElement('canvas');
                    canvas.width = width;
                    canvas.height = height;

                    const ctx = canvas.getContext('2d');
                    if (!ctx) {
                        reject(new Error('Canvas context error'));
                        return;
                    }

                    ctx.drawImage(img, 0, 0, width, height);

                    canvas.toBlob((blob) => {
                        if (!blob) {
                            reject(new Error('Image conversion failed'));
                            return;
                        }
                        const resizedFile = new File([blob], file.name, {
                            type: file.type,
                            lastModified: Date.now(),
                        });
                        resolve(resizedFile);
                    }, file.type, 0.9);
                };
                img.onerror = () => reject(new Error('Image load failed'));
                img.src = e.target?.result as string;
            };
            reader.onerror = () => reject(new Error('File read failed'));
            reader.readAsDataURL(file);
        });
    };

    const handleDragEnter = (e: React.DragEvent, ratio: number) => {
        e.preventDefault();
        e.stopPropagation();
        if (Math.abs(ratio - 3 / 4) < 0.01) setIsDragging34(true);
        else setIsDragging169(true);
    };

    const handleDragLeave = (e: React.DragEvent, ratio: number) => {
        e.preventDefault();
        e.stopPropagation();
        if (Math.abs(ratio - 3 / 4) < 0.01) setIsDragging34(false);
        else setIsDragging169(false);
    };

    const handleDragOver = (e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
    };

    const handleDrop = (e: React.DragEvent, ratio: number) => {
        e.preventDefault();
        e.stopPropagation();
        if (Math.abs(ratio - 3 / 4) < 0.01) setIsDragging34(false);
        else setIsDragging169(false);

        if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
            processFile(e.dataTransfer.files[0], ratio);
        }
    };

    const processFile = (file: File, ratio: number) => {
        const validImageTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
        if (!validImageTypes.includes(file.type)) {
            alert('지원되는 이미지 형식: JPG, PNG, GIF, WebP');
            return;
        }

        const reader = new FileReader();
        reader.onloadend = () => {
            const result = reader.result as string;
            const img = new Image();
            img.onload = async () => {
                const aspectRatio = img.width / img.height;
                const tolerance = 0.05;

                if (Math.abs(aspectRatio - ratio) > tolerance) {
                    setActiveCropRatio(ratio);
                    setTempImageForCrop(result);
                    setCropModalOpen(true);
                    return;
                }

                let processedFile = file;
                // Resize logic based on aspect ratio
                if (Math.abs(ratio - 3 / 4) < 0.01) {
                    // 3:4 -> Max Height 500
                    if (img.height > 500 || img.width > 375) {
                        processedFile = await resizeImage(file, 375, 500);
                    }
                } else {
                    // 16:9 -> Max Width 500
                    if (img.width > 500 || img.height > 282) {
                        processedFile = await resizeImage(file, 500, 282);
                    }
                }

                if (Math.abs(ratio - 3 / 4) < 0.01) {
                    setThumbnail34(prev => ({ ...prev, file: processedFile, preview: result }));
                } else {
                    setThumbnail169(prev => ({ ...prev, file: processedFile, preview: result }));
                }
            };
            img.src = result;
        };
        reader.readAsDataURL(file);
    };

    const handlePaste = (e: React.ClipboardEvent, ratio: number) => {
        const items = e.clipboardData.items;
        for (let i = 0; i < items.length; i++) {
            if (items[i].type.indexOf('image') !== -1) {
                const file = items[i].getAsFile();
                if (file) {
                    processFile(file, ratio);
                    e.preventDefault(); // Prevent default paste behavior
                }
                break;
            }
        }
    };

    const handleImageChange = (event: React.ChangeEvent<HTMLInputElement>, ratio: number) => {
        const file = event.target.files?.[0];
        if (file) {
            processFile(file, ratio);
        }
        event.target.value = '';
    };

    const handleCropComplete = async (croppedFile: File) => {
        const reader = new FileReader();
        reader.onloadend = async () => {
            const result = reader.result as string;

            // Resize cropped image if needed
            let finalFile = croppedFile;
            const img = new Image();
            img.onload = async () => {
                if (activeCropRatio && Math.abs(activeCropRatio - 3 / 4) < 0.01) {
                    // 3:4 -> Max Height 500
                    if (img.height > 500) {
                        finalFile = await resizeImage(croppedFile, 375, 500);
                    }
                    setThumbnail34({ ...thumbnail34, file: finalFile, preview: result });
                } else {
                    // 16:9 -> Max Width 500
                    if (img.width > 500) {
                        finalFile = await resizeImage(croppedFile, 500, 282);
                    }
                    setThumbnail169({ ...thumbnail169, file: finalFile, preview: result });
                }
                setCropModalOpen(false);
                setTempImageForCrop('');
                setActiveCropRatio(null);
            };
            img.src = result;
        };
        reader.readAsDataURL(croppedFile);
    };


    const handleCropCancel = () => {
        setCropModalOpen(false);
        setTempImageForCrop('');
        setActiveCropRatio(null);
    };

    const handleSubmit = async () => {
        // Validation
        if (!formData.title || !formData.youtubeUrl) {
            setError('제목과 YouTube URL은 필수입니다.');
            return;
        }

        if (!formData.youtubeVideoId) {
            setError('올바른 YouTube URL을 입력해주세요.');
            return;
        }

        try {
            setLoading(true);
            setError(null);

            // 로그인 시 선택한 테넌트 가져오기
            const SELECTED_TENANT_KEY = "mediinstep_selected_tenant";
            const savedTenantId = localStorage.getItem(SELECTED_TENANT_KEY);

            // 표시 순서 계산
            let calculatedDisplayOrder = 1;
            if (editingVideo) {
                // 편집 모드: 기존 displayOrder 유지
                calculatedDisplayOrder = editingVideo.displayOrder || 1;
            } else if (existingVideos.length > 0) {
                // 추가 모드: newVideoPosition을 기반으로 displayOrder 결정
                calculatedDisplayOrder = newVideoPosition + 1;
            }

            const basePayload = {
                TenantSeqId: savedTenantId ? parseInt(savedTenantId, 10) : null,
                Title: formData.title,
                Description: formData.description || null,
                YoutubeVideoId: formData.youtubeVideoId,
                YoutubeUrl: formData.youtubeUrl,
                ThumbnailUrl: formData.thumbnailUrl || null,
                DurationSec: formData.durationSec ? parseInt(formData.durationSec, 10) : null,
                Category: null as string | null,
                DifficultyLevel: null as string | null,
                Tags: null as string | null,
                DisplayOrder: calculatedDisplayOrder,
                PointPolicySeqId: formData.pointPolicySeqId ? parseInt(formData.pointPolicySeqId, 10) : null,
                IsActive: formData.isActive,
                IsFeatured: formData.isFeatured,
                BodyPartIds: formData.bodyPartIds,
            };

            let seqId = editingVideo?.seqId;
            let finalThumbnailUrl34 = thumbnail34.url;
            let finalThumbnailUrl169 = thumbnail169.url;

            // 1. Create Update Logic
            if (editingVideo) {
                // 편집 모드: 이미 seqId가 있음
                // 이미지 업로드 처리
                if (thumbnail34.file) {
                    const webpFile = await convertFileToWebP(thumbnail34.file);
                    // Rename: [seqId]_portrait.webp
                    const renamedFile = new File([webpFile], `${seqId}_portrait.webp`, { type: 'image/webp' });
                    const uploadResult = await uploadToS3(renamedFile, "video/portraitThumbnail");
                    finalThumbnailUrl34 = uploadResult.url;
                }

                if (thumbnail169.file) {
                    const webpFile = await convertFileToWebP(thumbnail169.file);
                    // Rename: [seqId]_landscape.webp
                    const renamedFile = new File([webpFile], `${seqId}_landscape.webp`, { type: 'image/webp' });
                    const uploadResult = await uploadToS3(renamedFile, "video/landscapeThumbnail");
                    finalThumbnailUrl169 = uploadResult.url;
                }

                // Update Video
                console.log('[Edit] Updating Video with thumbnails:', { seqId, finalThumbnailUrl34, finalThumbnailUrl169 });
                await request('ExerciseVideo.update', {
                    paramz: {
                        ...basePayload,
                        SeqId: seqId,
                        ThumbnailUrl43: finalThumbnailUrl34 || null,
                        ThumbnailUrl169: finalThumbnailUrl169 || null,
                        ThumbnailPortraitUrl: finalThumbnailUrl34 || null,
                        ThumbnailLandscapeUrl: finalThumbnailUrl169 || null,
                    },
                    pathParams: { id: seqId.toString() }
                });

            } else {
                // 추가 모드: 먼저 비디오 생성 후 seqId 획득
                const createRes = await request('ExerciseVideo.create', {
                    paramz: {
                        ...basePayload,
                        ThumbnailUrl43: null, // 일단 null로 생성
                        ThumbnailUrl169: null,
                    }
                });

                if (createRes && createRes.data) {
                    // API 응답 구조에 따라 seqId 추출
                    seqId = createRes.data.seqId || createRes.data;

                    if (!seqId) {
                        throw new Error("Failed to retrieve SeqId after creation.");
                    }

                    let needsUpdate = false;

                    // 이미지 업로드 처리
                    if (thumbnail34.file) {
                        const webpFile = await convertFileToWebP(thumbnail34.file);
                        const renamedFile = new File([webpFile], `${seqId}_portrait.webp`, { type: 'image/webp' });
                        const uploadResult = await uploadToS3(renamedFile, "video/portraitThumbnail");
                        finalThumbnailUrl34 = uploadResult.url;
                        needsUpdate = true;
                    }

                    if (thumbnail169.file) {
                        const webpFile = await convertFileToWebP(thumbnail169.file);
                        const renamedFile = new File([webpFile], `${seqId}_landscape.webp`, { type: 'image/webp' });
                        const uploadResult = await uploadToS3(renamedFile, "video/landscapeThumbnail");
                        finalThumbnailUrl169 = uploadResult.url;
                        needsUpdate = true;
                    }

                    // 이미지가 업로드되었다면 업데이트 호출
                    if (needsUpdate) {
                        console.log('[Create] Updating Video with uploaded thumbnails:', { seqId, finalThumbnailUrl34, finalThumbnailUrl169 });
                        await request('ExerciseVideo.update', {
                            paramz: {
                                ...basePayload,
                                SeqId: seqId,
                                DisplayOrder: calculatedDisplayOrder,
                                ThumbnailUrl43: finalThumbnailUrl34 || null,
                                ThumbnailUrl169: finalThumbnailUrl169 || null,
                                ThumbnailPortraitUrl: finalThumbnailUrl34 || null,
                                ThumbnailLandscapeUrl: finalThumbnailUrl169 || null,
                            },
                            pathParams: { id: seqId.toString() }
                        });
                    }
                }
            }

            setLoading(false);
            onSuccess();
            handleClose();
        } catch (err: any) {
            console.error('Exercise Video Error:', err);
            const errorMessage = err?.response?.data?.message || err.message || `운동 영상 ${editingVideo ? '수정' : '생성'} 중 오류가 발생했습니다.`;
            setError(errorMessage);
            setLoading(false);
        }
    };

    const handleClose = () => {
        setFormData({
            title: '',
            description: '',
            youtubeUrl: '',
            youtubeVideoId: '',
            thumbnailUrl: '',
            durationSec: '',

            displayOrder: '',
            pointPolicySeqId: '',
            isActive: true,
            isFeatured: false,
            bodyPartIds: [],
        });
        setThumbnail34({ url: '', file: null, preview: '' });
        setThumbnail169({ url: '', file: null, preview: '' });
        setError(null);
        onClose();
    };

    return (
        <>
            <Dialog
                open={open}
                onClose={handleClose}
                maxWidth="md"
                fullWidth
                PaperProps={{
                    sx: {
                        backgroundColor: darkMode ? '#1a2035' : '#fff',
                        backgroundImage: 'none'
                    }
                }}
            >
                {/* ... existing dialog content ... */}
                <DialogTitle sx={{ color: darkMode ? '#fff' : '#000' }}>
                    {editingVideo ? '운동 영상 수정' : '운동 영상 추가'}
                </DialogTitle>
                <DialogContent>
                    <MDBox component="form" role="form" mt={2}>
                        <Grid container spacing={2}>
                            {/* YouTube URL */}
                            <Grid item xs={12}>
                                <MDInput
                                    type="text"
                                    label="YouTube URL (Required)"
                                    name="youtubeUrl"
                                    value={formData.youtubeUrl}
                                    onChange={handleChange}
                                    fullWidth
                                    error={!!error && !formData.youtubeUrl}
                                    placeholder="https://www.youtube.com/watch?v=..."
                                />
                                {formData.youtubeVideoId && (
                                    <MDTypography variant="caption" color="success" sx={{ mt: 0.5, display: 'block' }}>
                                        ✓ Video ID: {formData.youtubeVideoId}
                                    </MDTypography>
                                )}
                                {fetchingVideoInfo && (
                                    <MDTypography variant="caption" color="info" sx={{ mt: 0.5, display: 'block' }}>
                                        🔄 YouTube에서 영상 정보를 가져오는 중...
                                    </MDTypography>
                                )}

                                {/* YouTube 영상 미리보기 */}
                                {formData.youtubeVideoId && (
                                    <MDBox sx={{ mt: 2, borderRadius: 2, overflow: 'hidden' }}>
                                        <iframe
                                            width="100%"
                                            height="315"
                                            src={`https://www.youtube.com/embed/${formData.youtubeVideoId}`}
                                            title="YouTube video preview"
                                            frameBorder="0"
                                            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                            allowFullScreen
                                            style={{
                                                borderRadius: '8px',
                                                border: darkMode ? '1px solid rgba(255, 255, 255, 0.12)' : '1px solid rgba(0, 0, 0, 0.12)'
                                            }}
                                        />
                                    </MDBox>
                                )}
                            </Grid>

                            {/* 제목 */}
                            <Grid item xs={12}>
                                <MDInput
                                    type="text"
                                    label="영상 제목 (Required)"
                                    name="title"
                                    value={formData.title}
                                    onChange={handleChange}
                                    fullWidth
                                    error={!!error && !formData.title}
                                />
                            </Grid>

                            {/* 영상 길이 */}
                            <Grid item xs={12}>
                                <MDInput
                                    type="number"
                                    label="영상 길이 (초)"
                                    name="durationSec"
                                    value={formData.durationSec}
                                    onChange={handleChange}
                                    fullWidth
                                    sx={{
                                        '& input[type=number]': {
                                            colorScheme: darkMode ? 'dark' : 'normal',
                                        },
                                    }}
                                />
                            </Grid>

                            {/* 관심 부위 (Body Parts) */}
                            <Grid item xs={12}>
                                <Autocomplete
                                    multiple
                                    options={bodyParts}
                                    getOptionLabel={(option) => option.name}
                                    value={bodyParts.filter(bp => formData.bodyPartIds.includes(bp.seqId))}
                                    onChange={(event, newValue) => {
                                        setFormData(prev => ({
                                            ...prev,
                                            bodyPartIds: newValue.map(item => item.seqId)
                                        }));
                                    }}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            label="관심 부위 (Body Parts)"
                                            placeholder="부위를 선택하세요"
                                            variant="outlined"
                                        />
                                    )}
                                    renderTags={(value, getTagProps) =>
                                        value.map((option, index) => (
                                            <Chip
                                                variant="outlined"
                                                label={option.name}
                                                {...getTagProps({ index })}
                                                size="small"
                                            />
                                        ))
                                    }
                                />
                            </Grid>

                            {/* 설명 */}
                            <Grid item xs={12}>
                                <MDInput
                                    type="text"
                                    label="설명"
                                    name="description"
                                    value={formData.description}
                                    onChange={handleChange}
                                    fullWidth
                                    multiline
                                    rows={3}
                                />
                            </Grid>

                            {/* 썸네일 미리보기 */}
                            {formData.thumbnailUrl && (
                                <Grid item xs={12}>
                                    <MDTypography variant="caption" color="text" sx={{ mb: 1, display: 'block' }}>
                                        YouTube 썸네일 미리보기
                                    </MDTypography>
                                    <MDBox
                                        sx={{
                                            width: '100%',
                                            maxWidth: 320,
                                            borderRadius: 2,
                                            overflow: 'hidden',
                                            border: darkMode ? '1px solid rgba(255, 255, 255, 0.12)' : '1px solid rgba(0, 0, 0, 0.12)',
                                        }}
                                    >
                                        <img
                                            src={formData.thumbnailUrl}
                                            alt="Video thumbnail"
                                            style={{
                                                width: '100%',
                                                height: 'auto',
                                                display: 'block',
                                            }}
                                        />
                                    </MDBox>
                                </Grid>
                            )}

                            {/* 3:4 썸네일 업로드 */}
                            <Grid item xs={12} sm={6}>
                                <MDBox mb={1}>
                                    <MDTypography variant="button" fontWeight="medium" color="text">
                                        3:4 썸네일
                                    </MDTypography>
                                </MDBox>
                                {!thumbnail34.preview ? (
                                    <MDBox
                                        onDragEnter={(e: React.DragEvent) => handleDragEnter(e, 3 / 4)}
                                        onDragLeave={(e: React.DragEvent) => handleDragLeave(e, 3 / 4)}
                                        onDragOver={handleDragOver}
                                        onDrop={(e: React.DragEvent) => handleDrop(e, 3 / 4)}
                                        onPaste={(e: React.ClipboardEvent) => handlePaste(e, 3 / 4)}
                                        tabIndex={0}
                                        sx={{
                                            height: 200,
                                            border: isDragging34
                                                ? darkMode ? "2px solid #49a3f1" : "2px solid #1976d2"
                                                : darkMode ? "1px dashed rgba(255, 255, 255, 0.3)" : "1px dashed rgba(0, 0, 0, 0.3)",
                                            borderRadius: "8px",
                                            p: 2,
                                            textAlign: "center",
                                            backgroundColor: isDragging34
                                                ? darkMode ? "rgba(73, 163, 241, 0.1)" : "rgba(25, 118, 210, 0.05)"
                                                : "transparent",
                                            transition: "all 0.2s ease-in-out",
                                            outline: "none",
                                            cursor: "default",
                                            display: 'flex',
                                            flexDirection: 'column',
                                            justifyContent: 'center',
                                            alignItems: 'center',
                                        }}
                                    >
                                        <input
                                            type="file"
                                            id="image-upload-34"
                                            hidden
                                            accept="image/*"
                                            onChange={(e) => handleImageChange(e, 3 / 4)}
                                        />
                                        <MDButton
                                            variant="outlined"
                                            component="span"
                                            color="info"
                                            startIcon={<CloudUploadIcon />}
                                            sx={{ pointerEvents: 'auto' }}
                                            onClick={() => document.getElementById('image-upload-34')?.click()}
                                        >
                                            이미지 선택
                                        </MDButton>
                                        <MDTypography
                                            variant="caption"
                                            color="text"
                                            sx={{
                                                display: "block",
                                                mt: 1,
                                                opacity: 0.7,
                                            }}
                                        >
                                            또는 이미지를 드래그하거나 Ctrl+V로 붙여넣기<br />
                                            지원 형식: JPG, PNG, GIF, WebP<br />
                                            비율: 3:4 세로 화면<br />
                                            세로 500px 초과 시 자동 리사이즈됩니다<br />
                                            파일을 드래그하여 놓거나 버튼을 클릭하세요
                                        </MDTypography>
                                    </MDBox>
                                ) : (
                                    <MDBox position="relative">
                                        <MDBox
                                            sx={{
                                                width: '100%',
                                                height: 200,
                                                borderRadius: 2,
                                                overflow: 'hidden',
                                                border: darkMode ? '1px solid rgba(255, 255, 255, 0.12)' : '1px solid rgba(0, 0, 0, 0.12)',
                                                display: 'flex',
                                                justifyContent: 'center',
                                                alignItems: 'center',
                                                backgroundColor: darkMode ? '#1a2035' : '#f0f2f5',
                                            }}
                                        >
                                            <img
                                                src={thumbnail34.preview}
                                                alt="3:4 Thumbnail"
                                                style={{
                                                    maxWidth: '100%',
                                                    maxHeight: '100%',
                                                    objectFit: 'contain',
                                                }}
                                            />
                                        </MDBox>
                                        <MDButton
                                            size="small"
                                            variant="contained"
                                            color="error"
                                            onClick={() => {
                                                setThumbnail34({ url: '', file: null, preview: '' });
                                                const input = document.getElementById('image-upload-34') as HTMLInputElement;
                                                if (input) input.value = '';
                                            }}
                                            sx={{
                                                position: 'absolute',
                                                top: 8,
                                                right: 8,
                                            }}
                                        >
                                            삭제
                                        </MDButton>
                                    </MDBox>
                                )}
                            </Grid>

                            {/* 16:9 썸네일 업로드 */}
                            <Grid item xs={12} sm={6}>
                                <MDBox mb={1}>
                                    <MDTypography variant="button" fontWeight="medium" color="text">
                                        16:9 썸네일
                                    </MDTypography>
                                </MDBox>
                                {!thumbnail169.preview ? (
                                    <MDBox
                                        onDragEnter={(e: React.DragEvent) => handleDragEnter(e, 16 / 9)}
                                        onDragLeave={(e: React.DragEvent) => handleDragLeave(e, 16 / 9)}
                                        onDragOver={handleDragOver}
                                        onDrop={(e: React.DragEvent) => handleDrop(e, 16 / 9)}
                                        onPaste={(e: React.ClipboardEvent) => handlePaste(e, 16 / 9)}
                                        tabIndex={0}
                                        sx={{
                                            height: 200,
                                            border: isDragging169
                                                ? darkMode ? "2px solid #49a3f1" : "2px solid #1976d2"
                                                : darkMode ? "1px dashed rgba(255, 255, 255, 0.3)" : "1px dashed rgba(0, 0, 0, 0.3)",
                                            borderRadius: "8px",
                                            p: 2,
                                            textAlign: "center",
                                            backgroundColor: isDragging169
                                                ? darkMode ? "rgba(73, 163, 241, 0.1)" : "rgba(25, 118, 210, 0.05)"
                                                : "transparent",
                                            transition: "all 0.2s ease-in-out",
                                            outline: "none",
                                            cursor: "default",
                                            display: 'flex',
                                            flexDirection: 'column',
                                            justifyContent: 'center',
                                            alignItems: 'center',
                                        }}
                                    >
                                        <input
                                            type="file"
                                            id="image-upload-169"
                                            hidden
                                            accept="image/*"
                                            onChange={(e) => handleImageChange(e, 16 / 9)}
                                        />
                                        <MDButton
                                            variant="outlined"
                                            component="span"
                                            color="info"
                                            startIcon={<CloudUploadIcon />}
                                            sx={{ pointerEvents: 'auto' }}
                                            onClick={() => document.getElementById('image-upload-169')?.click()}
                                        >
                                            이미지 선택
                                        </MDButton>
                                        <MDTypography
                                            variant="caption"
                                            color="text"
                                            sx={{
                                                display: "block",
                                                mt: 1,
                                                opacity: 0.7,
                                            }}
                                        >
                                            또는 이미지를 드래그하거나 Ctrl+V로 붙여넣기<br />
                                            지원 형식: JPG, PNG, GIF, WebP<br />
                                            비율: 16:9 가로 화면<br />
                                            가로 500px 초과 시 자동 리사이즈됩니다<br />
                                            파일을 드래그하여 놓거나 버튼을 클릭하세요
                                        </MDTypography>
                                    </MDBox>
                                ) : (
                                    <MDBox position="relative">
                                        <MDBox
                                            sx={{
                                                width: '100%',
                                                height: 200,
                                                borderRadius: 2,
                                                overflow: 'hidden',
                                                border: darkMode ? '1px solid rgba(255, 255, 255, 0.12)' : '1px solid rgba(0, 0, 0, 0.12)',
                                                display: 'flex',
                                                justifyContent: 'center',
                                                alignItems: 'center',
                                                backgroundColor: darkMode ? '#1a2035' : '#f0f2f5',
                                            }}
                                        >
                                            <img
                                                src={thumbnail169.preview}
                                                alt="16:9 Thumbnail"
                                                style={{
                                                    maxWidth: '100%',
                                                    maxHeight: '100%',
                                                    objectFit: 'contain',
                                                }}
                                            />
                                        </MDBox>
                                        <MDButton
                                            size="small"
                                            variant="contained"
                                            color="error"
                                            onClick={() => {
                                                setThumbnail169({ url: '', file: null, preview: '' });
                                                const input = document.getElementById('image-upload-169') as HTMLInputElement;
                                                if (input) input.value = '';
                                            }}
                                            sx={{
                                                position: 'absolute',
                                                top: 8,
                                                right: 8,
                                            }}
                                        >
                                            삭제
                                        </MDButton>
                                    </MDBox>
                                )}
                            </Grid>



                            {/* 영상 길이 */}
                            <Grid item xs={12} sm={6}>
                                <MDInput
                                    type="text"
                                    label="영상 길이 (HH:MM:SS)"
                                    name="durationDisplay"
                                    value={(() => {
                                        const totalSec = parseInt(formData.durationSec || '0', 10);
                                        const hours = Math.floor(totalSec / 3600);
                                        const minutes = Math.floor((totalSec % 3600) / 60);
                                        const seconds = totalSec % 60;
                                        return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
                                    })()}
                                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                        const value = e.target.value;
                                        // HH:MM:SS 형식 검증
                                        const match = value.match(/^(\d{0,2}):?(\d{0,2}):?(\d{0,2})$/);
                                        if (match) {
                                            const hours = parseInt(match[1] || '0', 10);
                                            const minutes = parseInt(match[2] || '0', 10);
                                            const seconds = parseInt(match[3] || '0', 10);
                                            const totalSeconds = hours * 3600 + minutes * 60 + seconds;
                                            setFormData(prev => ({ ...prev, durationSec: totalSeconds.toString() }));
                                        }
                                    }}
                                    fullWidth
                                    placeholder="00:05:30"
                                />
                                <MDTypography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                                    💡 시:분:초 형식으로 입력하세요 (예: 00:05:30)
                                </MDTypography>
                            </Grid>

                            {/* 표시 순서 - 드래그 앤 드롭 (추가 모드에만 표시) */}
                            {!editingVideo && (
                                <Grid item xs={12}>
                                    <DisplayOrderManager
                                        existingVideos={existingVideos}
                                        newVideoPosition={newVideoPosition}
                                        newVideoThumbnailUrl={formData.thumbnailUrl}
                                        onPositionChange={setNewVideoPosition}
                                        darkMode={darkMode}
                                    />
                                </Grid>
                            )}



                            {/* 적립정책 */}
                            <Grid item xs={12}>
                                <FormControl fullWidth>
                                    <InputLabel id="point-policy-label" sx={{ color: darkMode ? '#fff' : 'rgba(0, 0, 0, 0.6)' }}>
                                        적립정책 (가장 최근 정책 자동 할당)
                                    </InputLabel>
                                    <Select
                                        labelId="point-policy-label"
                                        value={formData.pointPolicySeqId}
                                        label="적립정책 (자동 할당)"
                                        onChange={handleSelectChange('pointPolicySeqId')}
                                        sx={{ height: '44px' }}
                                        disabled // User cannot change this
                                    >
                                        <MenuItem value="">선택 안함</MenuItem>
                                        {pointPolicies.map((policy) => (
                                            <MenuItem key={policy.seqId} value={policy.seqId.toString()}>
                                                {policy.policyName} ({policy.pointAmount}P)
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Grid>



                            {/* 에러 메시지 */}
                            {error && (
                                <Grid item xs={12}>
                                    <MDBox
                                        sx={{
                                            p: 2,
                                            borderRadius: 1,
                                            backgroundColor: 'rgba(244, 67, 54, 0.1)',
                                            border: '1px solid rgba(244, 67, 54, 0.5)',
                                        }}
                                    >
                                        <MDTypography variant="body2" color="error">
                                            {error}
                                        </MDTypography>
                                    </MDBox>
                                </Grid>
                            )}
                        </Grid>
                    </MDBox>
                </DialogContent>
                <DialogActions>
                    <MDButton onClick={handleClose} color="secondary">
                        취소
                    </MDButton>
                    <MDButton
                        onClick={handleSubmit}
                        variant="gradient"
                        color="info"
                        disabled={loading}
                    >
                        {loading
                            ? (editingVideo ? '수정 중...' : '생성 중...')
                            : (editingVideo ? '수정' : '생성')
                        }
                    </MDButton>
                </DialogActions>
            </Dialog>
            {cropModalOpen && activeCropRatio && (
                <ImageCropModal
                    open={cropModalOpen}
                    imageSrc={tempImageForCrop}
                    onClose={handleCropCancel}
                    onCropComplete={handleCropComplete}
                    darkMode={darkMode}
                    aspectRatio={activeCropRatio}
                />
            )}
        </>
    );
};

// Sortable Item Component
interface SortableItemProps {
    id: string;
    title: string;
    thumbnailUrl?: string;
    isNewVideo: boolean;
    darkMode: boolean;
}

function SortableItem({ id, title, thumbnailUrl, isNewVideo, darkMode }: SortableItemProps) {
    const {
        attributes,
        listeners,
        setNodeRef,
        transform,
        transition,
        isDragging,
    } = useSortable({ id });

    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.5 : 1,
    };

    return (
        <MDBox
            ref={setNodeRef}
            style={style}
            {...attributes}
            {...(isNewVideo ? listeners : {})}
            sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
                p: 1.5,
                mb: 1,
                borderRadius: '8px',
                border: isNewVideo
                    ? darkMode
                        ? '2px dashed #49a3f1'
                        : '2px dashed #1976d2'
                    : darkMode
                        ? '1px solid rgba(255, 255, 255, 0.2)'
                        : '1px solid rgba(0, 0, 0, 0.2)',
                backgroundColor: isNewVideo
                    ? darkMode
                        ? 'rgba(73, 163, 241, 0.1)'
                        : 'rgba(25, 118, 210, 0.05)'
                    : darkMode
                        ? 'rgba(255, 255, 255, 0.05)'
                        : 'rgba(0, 0, 0, 0.02)',
                cursor: isNewVideo ? 'grab' : 'default',
                '&:active': {
                    cursor: isNewVideo ? 'grabbing' : 'default',
                },
            }}
        >
            <DragIndicatorIcon
                sx={{
                    color: isNewVideo
                        ? darkMode ? 'rgba(255, 255, 255, 0.8)' : 'rgba(0, 0, 0, 0.8)'
                        : darkMode ? 'rgba(255, 255, 255, 0.3)' : 'rgba(0, 0, 0, 0.3)',
                }}
            />
            {thumbnailUrl && (
                <MDBox
                    component="img"
                    src={thumbnailUrl}
                    alt={title}
                    sx={{
                        width: 60,
                        height: 45,
                        objectFit: 'cover',
                        borderRadius: '4px',
                        flexShrink: 0,
                    }}
                />
            )}
            <MDTypography
                variant="button"
                fontWeight={isNewVideo ? 'bold' : 'regular'}
                sx={{
                    color: isNewVideo
                        ? darkMode
                            ? '#49a3f1'
                            : '#1976d2'
                        : darkMode
                            ? 'rgba(255, 255, 255, 0.8)'
                            : '#344767',
                    flex: 1,
                }}
            >
                {isNewVideo ? '🆕 ' : ''}{title}
            </MDTypography>
        </MDBox>
    );
}

// Display Order Manager Component
interface DisplayOrderManagerProps {
    existingVideos: Array<{ seqId: number; title: string; displayOrder: number; thumbnailUrl?: string }>;
    newVideoPosition: number;
    newVideoThumbnailUrl?: string;
    onPositionChange: (newPosition: number) => void;
    darkMode: boolean;
}

function DisplayOrderManager({
    existingVideos,
    newVideoPosition,
    newVideoThumbnailUrl,
    onPositionChange,
    darkMode,
}: DisplayOrderManagerProps) {
    const sensors = useSensors(
        useSensor(PointerSensor),
        useSensor(KeyboardSensor, {
            coordinateGetter: sortableKeyboardCoordinates,
        })
    );

    const items = [
        ...existingVideos.slice(0, newVideoPosition).map((video) => ({
            id: `existing-${video.seqId}`,
            title: video.title,
            thumbnailUrl: video.thumbnailUrl,
            isNewVideo: false,
        })),
        {
            id: 'new-video',
            title: '새 영상',
            thumbnailUrl: newVideoThumbnailUrl,
            isNewVideo: true,
        },
        ...existingVideos.slice(newVideoPosition).map((video) => ({
            id: `existing-${video.seqId}`,
            title: video.title,
            thumbnailUrl: video.thumbnailUrl,
            isNewVideo: false,
        })),
    ];

    const handleDragEnd = (event: DragEndEvent) => {
        const { active, over } = event;

        if (over && active.id !== over.id && active.id === 'new-video') {
            const oldIndex = items.findIndex((item) => item.id === active.id);
            const newIndex = items.findIndex((item) => item.id === over.id);

            const reorderedItems = arrayMove(items, oldIndex, newIndex);
            const newVideoIndexInReordered = reorderedItems.findIndex((item) => item.id === 'new-video');

            let actualPosition = 0;
            for (let i = 0; i < newVideoIndexInReordered; i++) {
                if (reorderedItems[i].id !== 'new-video') {
                    actualPosition++;
                }
            }
            onPositionChange(actualPosition);
        }
    };

    return (
        <MDBox>
            <MDTypography
                variant="caption"
                fontWeight="regular"
                sx={{ color: darkMode ? 'rgba(255, 255, 255, 0.8)' : '#344767' }}
                mb={1}
            >
                영상 표시 순서 (드래그하여 위치 조정)
            </MDTypography>
            <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragEnd={handleDragEnd}
            >
                <SortableContext
                    items={items.map((item) => item.id)}
                    strategy={verticalListSortingStrategy}
                >
                    {items.map((item) => (
                        <SortableItem
                            key={item.id}
                            id={item.id}
                            title={item.title}
                            thumbnailUrl={item.thumbnailUrl}
                            isNewVideo={item.isNewVideo}
                            darkMode={darkMode}
                        />
                    ))}
                </SortableContext>
            </DndContext>
            <MDTypography
                variant="caption"
                sx={{ color: darkMode ? 'rgba(255, 255, 255, 0.6)' : 'rgba(0, 0, 0, 0.6)' }}
                display="block"
                mt={1}
            >
                💡 새 영상을 드래그하여 원하는 위치에 배치하세요
            </MDTypography>
        </MDBox>
    );
}

export default AddExerciseVideoModal;
